import React, { useMemo } from 'react';
import * as THREE from 'three';
import { BeveledPanel } from '../FabProcessTool/BeveledPanel';
import {
  getBrushedAluminum,
  getCyanLedMaterial,
  getGraphiteMetal,
} from '../FabProcessTool/equipmentMaterials';
import type { Awp300Layout } from './awp300Layout';

export type Awp300ChamberProps = {
  layout: Awp300Layout;
  opacity?: number;
  ledIntensity?: number;
};

/** Central circular wafer process chamber — reference mid-height platform. */
export const Awp300Chamber: React.FC<Awp300ChamberProps> = ({
  layout,
  opacity = 1,
  ledIntensity = 1,
}) => {
  const graphite = useMemo(() => getGraphiteMetal(opacity), [opacity]);
  const brushed = useMemo(() => getBrushedAluminum(opacity), [opacity]);
  const cyan = useMemo(() => getCyanLedMaterial(ledIntensity, opacity), [ledIntensity, opacity]);

  const { chamberY, chamberR, frameT } = layout;
  const platH = frameT * 0.55;

  return (
    <group name="Awp300Chamber" position={[0, chamberY, 0]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} material={graphite} receiveShadow castShadow>
        <cylinderGeometry args={[chamberR, chamberR * 1.02, platH, 32]} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, platH * 0.55, 0]} material={brushed}>
        <cylinderGeometry args={[chamberR * 0.82, chamberR * 0.82, platH * 0.35, 32]} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, platH * 0.75, 0]}>
        <ringGeometry args={[chamberR * 0.35, chamberR * 0.78, 32]} />
        <meshStandardMaterial
          color="#0A1820"
          emissive="#28D7FF"
          emissiveIntensity={0.85 * ledIntensity}
          toneMapped={false}
          transparent={opacity < 1}
          opacity={opacity}
          side={THREE.DoubleSide}
        />
      </mesh>
      {[0, 1, 2, 3].map((i) => {
        const a = (i / 4) * Math.PI * 2;
        return (
          <BeveledPanel
            key={`chamber-led-${i}`}
            size={[frameT * 0.35, frameT * 0.35, frameT * 0.35]}
            material={cyan}
            position={[Math.cos(a) * chamberR * 0.88, platH * 0.4, Math.sin(a) * chamberR * 0.88]}
          />
        );
      })}
    </group>
  );
};

export default Awp300Chamber;
